import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { cancelFunctions } from 'utils/cancelFunctions';
import { Store } from 'components/common/types/Store.types';
import {
  addCreativeDeal,
  fetchCreativeDetails,
  getAllFrameTags,
  getCreativeCategories,
  getCreativeFrameSummary,
  removeCreativeDeal,
} from 'modules/api/content';
import { updateCreativeMovement } from 'modules/api/previewContent';
import Local from 'modules/Local';
import { AuditDetails, CreativeMarketDeal, CreativeMarketTag } from 'components/common/types/Creative.types';
import { formatDateNoUTC, formatTime } from 'modules/I18N';
import { CodeNameModel } from 'components/common/types';
import { setCreativeDetails, setFrameSummary, setMovement, updateCreativeProperties } from 'store/creative/reducer';
import Auth from 'modules/Auth';
import { PermissionsEnum } from 'consts/permissions';
import { getContentDealIdLookupData } from 'modules/api/lookups';
import { first, map, differenceBy } from 'lodash';
import { getDealsByOrigin } from 'store/creative/selectors';
import { MotionType } from 'consts/motionType';
import { addToastNotification } from 'store/notification/reducer';
import { isCanceledError } from 'modules/api/request';
import { getIsReseller } from 'store/publisher/selectors';
import DataListProps from 'lib/DataList/DataList.types';
import { filterDealLookupData } from 'components/pages/ContentManagement/utils/utils';
import {
  combineAuditDetails,
  combineAuditDetailsReseller,
} from 'components/pages/ContentManagement/Preview/PreviewContent/utils';

export type UsePreviewContentData = {
  isFetchingCreativeDetails: boolean;
  isLoadingDealOptions: boolean;
  combinedAuditDetails: AuditDetails[] | [];
  defaultDealOptions: DataListProps[];
  loadCreativesDetails: () => Promise<void>;
  getFrameSummary: () => Promise<void>;
  filterLoadedLookupData: (_dropdownName: string, qry: string) => Promise<DataListProps[]>;
  onSelectDealId: (selectedValue: Array<CreativeMarketDeal>) => Promise<void>;
  onMotionChange: (motionValue: MotionType) => Promise<void>;
};

export const usePreviewContentData = (): UsePreviewContentData => {
  const dispatch = useDispatch();
  const pathParams = useParams();

  const { localeCode } = useSelector((state: Store) => state.publisher.configuration);
  const environmentId = useSelector((state: Store) => state.environment.environmentId);
  const creativeDsp = useSelector((state: Store) => state.creative.dsp);
  const creativeMarket = useSelector((state: Store) => state.creative.market);
  const creativeMarketId = useSelector((state: Store) => state.creative.marketId);
  const creativeMovement = useSelector((state: Store) => state.creative.movement);
  const { ownDealIds, inheritedDealIds } = useSelector(getDealsByOrigin);
  const isReseller = useSelector(getIsReseller);

  const [isFetchingCreativeDetails, setIsFetchingCreativeDetails] = useState(false);
  const [isLoadingDealOptions, setIsLoadingDealOptions] = useState(false);
  const [combinedAuditDetails, setCombinedAuditDetails] = useState<AuditDetails[] | []>([]);
  const [defaultDealOptions, setDefaultDealOptions] = useState<DataListProps[]>([]);

  const creativeId = pathParams.id;
  const currentCreativeIdRef = useRef(creativeId);
  useEffect(() => {
    currentCreativeIdRef.current = creativeId;
  }, [creativeId]);

  const getFrameSummary = async (): Promise<void> => {
    if (!Auth.hasPermission(PermissionsEnum.CONTENT_WHITELIST_BACKLIST)) return;

    const requestCreativeId = creativeId;
    setIsFetchingCreativeDetails(true);

    try {
      const frameSummary = await getCreativeFrameSummary(cancelFunctions, creativeId, Local.getEnvironmentId());

      dispatch(setFrameSummary(frameSummary));
    } catch (error: unknown) {
      if (!isCanceledError(error)) {
        dispatch(
          addToastNotification({
            error: error as Error,
            timeout: 10000,
            type: 'Failure',
          }),
        );
      }
    } finally {
      setIsFetchingCreativeDetails(false);
    }

    if (currentCreativeIdRef.current === requestCreativeId) {
      setIsFetchingCreativeDetails(false);
    }
  };

  const loadCreativesDetails = async (): Promise<void> => {
    const requestCreativeId = creativeId;
    setIsFetchingCreativeDetails(true);

    try {
      const creativeDetails = await fetchCreativeDetails(cancelFunctions, creativeId);

      if (currentCreativeIdRef.current !== requestCreativeId) return;

      const currentMarket = creativeDetails?.market.find(
        ({ environment }: { environment: string }) => environment === environmentId,
      );

      const auditDetails = isReseller
        ? combineAuditDetailsReseller(currentMarket, creativeDetails, environmentId)
        : combineAuditDetails(currentMarket.moderation, currentMarket.auditLogs);

      setCombinedAuditDetails(auditDetails);

      const additionalProductCategoriesOptions = await getCreativeCategories();
      const productCategory = currentMarket?.commercial?.productCategory;
      const processedAdditionalProductCategoriesOptions = productCategory?.id
        ? additionalProductCategoriesOptions.filter((category: CodeNameModel) => category.id !== productCategory.id)
        : [...additionalProductCategoriesOptions];
      const commercialId = currentMarket?.commercial?.id;
      const brand = currentMarket?.commercial?.brand;
      const marketId = currentMarket?.id;
      const tags = marketId ? await getAllFrameTags(cancelFunctions, marketId) : [];

      const processedCreativeDetails = {
        marketId,
        deleted: currentMarket?.deleted,
        deletedAt: currentMarket?.deletedAt,
        convertStatus: creativeDetails?.convertStatus,
        statusCode: currentMarket?.moderation[0].status,
        dealId:
          currentMarket?.deal?.map((deal: CreativeMarketDeal) => ({
            code: deal.id,
            name: deal.dealId,
            inherited: deal.inherited,
          })) ?? null,
        productFormat: currentMarket?.productFormat,
        moderation: currentMarket?.moderation,
        commercialId,
        externalAdvertiserName: creativeDetails.externalAdvertiserName,
        clickThroughUrl: creativeDetails.clickThroughUrl,
        externalAgencyId: creativeDetails.externalAgencyId,
        advertiser: currentMarket?.commercial?.advertiser,
        frameTags: tags?.map((tag: CreativeMarketTag) => ({ ...tag.tag, type: tag.type, marketTagId: tag.id })),
        brand,
        productCategory,
        additionalProductCategories: currentMarket?.productCategory,
        agency: currentMarket?.agency,
        specialist: currentMarket?.specialist,
        externalId: creativeDetails.externalId,
        dsp: creativeDetails.dsp,
        width: creativeDetails.width,
        height: creativeDetails.height,
        uploaded: creativeDetails.uploaded,
        uploadedBy: creativeDetails.uploadedBy?.name,
        uploadedDate: formatDateNoUTC(creativeDetails.uploaded, localeCode),
        uploadedTime: formatTime(creativeDetails.uploaded, localeCode),
        movement: creativeDetails.movement,
        contentURL: creativeDetails.contentURL,
        downloadURL: creativeDetails.downloadURL,
        thumbnail: creativeDetails.thumbnail,
        type: creativeDetails.type,
        additionalProductCategoriesOptions: processedAdditionalProductCategoriesOptions,
        allAdditionalProductCategoriesOptions: additionalProductCategoriesOptions,
        market: creativeDetails.market,
        productCategoriesOptions: commercialId && productCategory ? [productCategory] : [],
      };

      dispatch(setCreativeDetails(processedCreativeDetails));

      await getFrameSummary();
    } catch (error: unknown) {
      if (!isCanceledError(error)) {
        dispatch(
          addToastNotification({
            error: error as Error,
            timeout: 10000,
            type: 'Failure',
          }),
        );
      }
    } finally {
      if (currentCreativeIdRef.current === requestCreativeId) {
        setIsFetchingCreativeDetails(false);
      }
    }
  };

  useEffect(() => {
    if (!creativeDsp.code) return;

    const loadDefaultDealOptions = async (): Promise<void> => {
      setIsLoadingDealOptions(true);

      try {
        const dealIdLookupData = await getContentDealIdLookupData(
          cancelFunctions,
          environmentId,
          null,
          creativeDsp?.code,
          true,
        );
        if (dealIdLookupData) setDefaultDealOptions(dealIdLookupData);
        // eslint-disable-next-line no-empty
      } catch {
      } finally {
        setIsLoadingDealOptions(false);
      }
    };

    loadDefaultDealOptions();
  }, [creativeDsp.code]);

  const onSelectDealId = async (selectedValue: Array<CreativeMarketDeal>): Promise<void> => {
    const dealIdsToRemove = map(differenceBy(ownDealIds, selectedValue, 'dealId'), 'id');
    const dealIdToAdd = first(map(differenceBy(selectedValue, ownDealIds, 'dealId'), 'dealId'));

    try {
      if (dealIdToAdd) {
        const result = await addCreativeDeal(cancelFunctions, creativeMarketId, dealIdToAdd);
        const currentMarketUpdated = result.market.find(
          ({ environment }: { environment: string }) => environment === Local.getEnvironmentId(),
        );
        const newDealId = currentMarketUpdated.deal ?? null;

        dispatch(updateCreativeProperties({ dealId: newDealId, market: result.market }));
      } else if (dealIdsToRemove.length) {
        await Promise.all(
          dealIdsToRemove.map(async (id) => {
            await removeCreativeDeal(cancelFunctions, id);
          }),
        );

        const updatedMarket = creativeMarket.map((marketObj) => {
          let updatedDeal = [...marketObj.deal];
          if (marketObj.environment === Local.getEnvironmentId()) {
            updatedDeal = marketObj.deal.filter((d) => !dealIdsToRemove.includes(d.id));
          }
          return { ...marketObj, deal: updatedDeal };
        });
        dispatch(updateCreativeProperties({ dealId: [...inheritedDealIds, ...selectedValue], market: updatedMarket }));
      }
    } catch {} // eslint-disable-line no-empty
  };

  const onMotionChange = async (motionValue: MotionType): Promise<void> => {
    try {
      if (creativeMovement === motionValue) return;

      await updateCreativeMovement(cancelFunctions, { creativeId: creativeId ?? '', movement: motionValue });

      dispatch(setMovement(motionValue));

      await getFrameSummary();
      // eslint-disable-next-line no-empty
    } catch (error) {}
  };

  const filterLoadedLookupData = async (_dropdownName: string, qry: string): Promise<DataListProps[]> =>
    filterDealLookupData(qry, defaultDealOptions);

  return {
    isFetchingCreativeDetails,
    isLoadingDealOptions,
    combinedAuditDetails,
    defaultDealOptions,
    loadCreativesDetails,
    getFrameSummary,
    filterLoadedLookupData,
    onMotionChange,
    onSelectDealId,
  };
};
